var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { query } from 'lit/decorators/query.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ArrowNavigationController } from '../../controllers/arrowNavigation';
import { AutoPositionController } from '../../controllers/autoPositionOverlay';
import { SlotController } from '../../controllers/slot-controller';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { isPopoverSupported } from '../../util/popover';
import { BlocksElement } from '../BlocksElement';
import '../drawer/drawer';
import '../dropdown-panel/dropdown-panel';
import styles from './contextual-menu.styles';
/**
 * @slot trigger - the trigger element which toggles contextual-menu on click.
 * @slot default - The items to be displayed inside dropdown-panel.
 * @slot header - The items to be displayed inside header part in mobile view.
 * @fires bl-contextual-menu-open - emitted when contextual-menu is opened.
 * @fires bl-contextual-menu-close - emitted when contextual-menu is closed.
 * @csspart base - The component's base wrapper.
 * @csspart part-trigger - The component's trigger slot.
 * @csspart part-header - The component's header slot on orientation bottom.
 * @csspart bottom-drawer - The component's orientation bottom.
 * @csspart dropdown-panel - The component's dropdown-panel
 * @csspart part-default - The component's default slot.
 */
export class BlocksContextualMenu extends BlocksElement {
    constructor() {
        super(...arguments);
        /**
         * Positions the dropdown panel absolutely to the button.
         * <cat-text-passage size="sm">
         * <ul>
         * <li>**auto** places the dropdown based on the space available in the screen</li>
         * <li>**bottom** places the dropdown panel to the bottom center position</li>
         * <li>**bottom-start** places the dropdown panel to the starting of the bottom position</li>
         * <li>**bottom-end** places the dropdown panel to the ending of the bottom position</li>
         * <li>**top** places the dropdown panel to the top center position</li>
         * <li>**top-start** places the dropdown panel to the starting point of top position</li>
         * <li>**top-end** places the dropdown panel to the end point of top position</li>
         * <li>**left** places the dropdown panel to the left</li>
         * <li>**right** places the dropdown panel to the right</li>
         * </ul>
         * If `isInline` is false, and if specified position doesn't fit inside the viewport,
         * it will try to accommodate in other positions which fits best.
         * </cat-text-passage>
         */
        this.position = 'auto';
        /**
         * Determines the behavior of the panel based on the value of the `isInline` property.
         * If `isInline` is false and the popover attribute is supported by the browser,
         * the panel will be opened inside the top layer.
         * If none of the position fits and if it goes out of viewport, scroll will be enabled if `isInline` is false.
         * If `isInline` is true, the popover attribute will not be used, and the panel
         * will use the floating UI absolute strategy.
         *  @property {Boolean} isInline - Default is false.
         */
        this.isInline = false;
        /**
         * Enable an option to have drawer open from bottom in mobile
         */
        this.orientation = 'side';
        /**
         * Set max Height for orientation bottom drawer
         */
        this.verticalMaxHeight = 'default';
        /**
         * Mobile Variant
         */
        this.isMobile = false;
        /**
         * Breakpoint below which contextual-menu panel switches to drawer view**
         */
        this.mobileBreakPoint = MOBILE_BREAK_POINT;
        /**
         * Identify the current focused index
         */
        this.currentFocusedIndex = -1;
        this.autoPositionController = new AutoPositionController(this);
        this.arrowNavigationController = new ArrowNavigationController();
        this.slots = new SlotController(this, null, 'trigger', 'header');
        this.isPopOverSupported = isPopoverSupported();
        this.wasActive = false;
        /**
         * Sets mobile variant to have bottom drawer when orientation is bottom
         */
        this.triggerSmallScreen = () => {
            const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
            if (width < this.mobileBreakPoint && this.orientation === 'bottom') {
                this.isMobile = true;
                this.isActive && this.hidePopup();
            }
            else {
                this.isMobile = false;
                this.isActive && this.openContextualMenu();
            }
        };
        /**
         * Handle click outside the component
         * 1) Close the show hide panel on click outside
         * 2) If the nav is already closed then we don't care about outside clicks and we
         * can bail early
         * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
         * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
         * undefined. To work around that we'll check that we have a shadowRoot (and a
         * rendered .host) element here to appease the TypeScript compiler. This should never
         * actually be shown or run for a human end user.
         * 4) Check to see if we clicked inside the active panel
         * 5) If the panel is active and we've clicked outside of the panel then it should
         * be closed.
         */
        this.handleOnClickOutside = (event) => {
            /* 2 */
            if (!this.isActive) {
                return;
            }
            /* 3 */
            if (!this.shadowRoot?.host) {
                throw Error('Could not determine panel context during click handler');
            }
            const el = event.composedPath();
            /* 4 */
            const didClickInside = el.includes(this.shadowRoot.host);
            /* 5 */
            if (this.isActive && !didClickInside) {
                this.toggleActive();
            }
        };
        /**
         * Set menu active state
         * 1) Set position to original stated position and Set attribute since aria expanded can't be passed down through the slot
         * 2) If the active state is toggled to false, close the panel and return focus to the dropdown trigger
         */
        this.toggleActive = () => {
            if (!this.isActive) {
                this.open(true); /* 1 */
            }
            else {
                this.close(); /* 2 */
            }
        };
    }
    static { this.styles = styles; }
    get _catDropdownPanelBody() {
        return this.shadowRoot?.querySelector('cat-dropdown-panel')?.shadowRoot?.querySelector('.cat-c-dropdown-panel__body');
    }
    get isPopoverEnabled() {
        return this.isPopOverSupported && !this.isInline;
    }
    get _catListItems() {
        return Array.from(this.querySelectorAll('cat-list-item')) || [];
    }
    get _catList() {
        return this.querySelectorAll('cat-list');
    }
    /**
     * Query the checkbox list to filter out disabled items that do not have hashOver set to true.
     */
    get _checkboxList() {
        return (Array.from(this.querySelectorAll('cat-checkbox-field')) || []).filter((item) => !item.disabled || item.hasHover);
    }
    getFloatingElement() {
        return this._catDynamicDropdownPanel;
    }
    getPosition() {
        return this.position;
    }
    getIsInline() {
        return this.isInline;
    }
    getGap() {
        return { mainAxis: 6, crossAxis: 0 };
    }
    getReferenceElement() {
        const catTriggerElement = this.querySelector('[slot="trigger"]');
        return catTriggerElement;
    }
    getOverflowElement() {
        return this._catDropdownPanelBody;
    }
    /**
     * Connected callback lifecycle
     * 1) Add event listeners
     */
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('mousedown', this.handleOnClickOutside, false);
        window.addEventListener('resize', this.triggerSmallScreen);
    }
    /**
     * Disconnected callback lifecycle
     * 1) Remove event listeners
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('mousedown', this.handleOnClickOutside, false);
        window.removeEventListener('resize', this.triggerSmallScreen);
    }
    /**
     * will update lifecycle on component initial load
     * 1) Add aria attribute values to the trigger button and list
     */
    willUpdate() {
        setTimeout(() => {
            this.addAria(); /* 1 */
            this.setListAriaRoles();
        }, 1);
        this.triggerSmallScreen();
    }
    setListAriaRoles() {
        this._catList?.forEach((item) => {
            item.listRole = 'menu';
        });
        this._catListItems?.forEach((item) => {
            item.listItemRole = 'menuitem';
        });
    }
    updated(_changedProperties) {
        if (_changedProperties.has('isActive')) {
            this.wasActive = _changedProperties.get('isActive') === undefined ? false : true;
            if (this.isActive) {
                this.openContextualMenu();
            }
            else {
                this.closeContextualMenu();
            }
        }
        if (_changedProperties.has('orientation')) {
            this.addAria();
        }
    }
    /**
     * Add/Remove aria expanded to the trigger button
     * 1) Select the button or cat-button within the trigger slot
     * 2) Set aria-pressed on the button trigger to the active state if provided. Otherwise, set to false.
     * 3) Set aria-expanded on the button trigger to the active state if provided. Otherwise, set to false.
     * Also, aria-haspopup be true to since it has interactive menus
     * 4) Remove aria-expanded/aria-haspopup on the trigger element when orientation is bottom.
     */
    addAria() {
        const triggerElement = this.querySelector('[slot="trigger"]'); /* 1 */
        if (triggerElement?.nodeName === 'CAT-BUTTON') {
            const buttonTrigger = triggerElement;
            if (this.orientation === 'bottom' && this.isMobile) {
                buttonTrigger.hasPopup = undefined; /* 4 */
                buttonTrigger.expanded = undefined; /* 4 */
            }
            else {
                buttonTrigger.expanded = this.isActive ? true : false; /* 3 */
                buttonTrigger.hasPopup = true; /* 3 */
            }
        }
        else {
            if (this.orientation === 'bottom' && this.isMobile) {
                triggerElement?.removeAttribute('aria-haspoup'); /* 4 */
                triggerElement?.removeAttribute('aria-expanded'); /* 4 */
            }
            else {
                triggerElement?.setAttribute('aria-expanded', `${this.isActive ? this.isActive : false}`); /* 3 */
                triggerElement?.setAttribute('aria-haspopup', 'true'); /* 3 */
            }
        }
    }
    /**
     * Open Contextual Menu
     * 1) fire `bl-contextual-menu-open` event based on `emitEvent` argument value. Event is emitted always internally as `emitEvent` is true by default.
     * 2) Query the panel-component which we are going to teleport into an overlay.
     * 3) Since the slotted light DOM element will not be a part of the default slot,
     * we need to query all the light DOM elements projected into the slot and append it to default slot.
     */
    open(emitEvent = false) {
        /** 1 */
        if (emitEvent) {
            const activeEvent = this.dispatch({
                eventName: 'bl-contextual-menu-open',
                detailObj: { isActive: true },
                optionsObj: { cancelable: true }
            });
            if (activeEvent.defaultPrevented) {
                return;
            }
        }
        this.isActive = true;
    }
    async openContextualMenu() {
        this.addAria();
        await this.updateComplete;
        this.dynamicPosition();
    }
    /**
     * Close Contextual Menu
     */
    close() {
        this.isActive = false;
        this.dispatch({ eventName: 'bl-contextual-menu-close', detailObj: { isActive: false } });
    }
    closeContextualMenu() {
        this._catListItems?.forEach((listItem) => {
            if (listItem.isActive && listItem.behavior === 'flyout') {
                listItem.isActive = false;
            }
        });
        this.currentFocusedIndex = -1;
        this.hidePopup();
        this.addAria();
        setTimeout(() => {
            if (this.wasActive) {
                this.querySelector('[slot="trigger"]')?.focus();
            }
        });
    }
    /**
     * Handle all dynamic placement
     */
    dynamicPosition() {
        const catTriggerElement = this.querySelector('[slot="trigger"]');
        if (this._catDynamicDropdownPanel && catTriggerElement) {
            this.autoPositionController.openOverlay();
            this.focusOnFirstContextualItem();
        }
    }
    /**
     * 1. Focuses the first listitem on Enter/Space key press
     * 2. Focuses the first Checkbox item on Enter/Space key press
     */
    focusOnFirstContextualItem() {
        /* 1 */
        if (this._catListItems.length > 0) {
            this._catListItems[0].focusOnFirstItem();
            return;
        }
        /* 2 */
        if (this._checkboxList.length > 0) {
            this.currentFocusedIndex = 0;
            this._checkboxList[this.currentFocusedIndex].focus();
        }
    }
    /**
     * Hide dropdown-panel popup
     */
    hidePopup() {
        this.autoPositionController.closeOverlay();
    }
    /**
     * Handle on keydown
     * 1) If the panel is open and escape is keyed, close the menu and return focus to the trigger button
     * 2) If the last element is defined, once Tab is selected after that the panel will close
     *  3) Prevents "Tab" key behavior on mobile when orientation is "bottom", allows it otherwise.
     */
    handleKeyDown(eventKeyDown) {
        if (this.isActive === true) {
            /*3*/
            if (this.isMobile && eventKeyDown.key === KEYS.TAB && this.orientation === 'bottom') {
                return;
            }
            if (eventKeyDown.key === KEYS.ESCAPE || eventKeyDown.key === KEYS.TAB) {
                eventKeyDown.stopPropagation();
                this.toggleActive();
            }
            if (eventKeyDown.key === KEYS.DOWN_ARROW || eventKeyDown.key === KEYS.UP_ARROW) {
                if (this._checkboxList.length > 0) {
                    this.handleCheckboxNavigation(eventKeyDown);
                }
            }
        }
    }
    /**
     * checkbox-field navigation
     */
    handleCheckboxNavigation(e) {
        this.currentFocusedIndex = this.arrowNavigationController.checkboxArrowNavigation(e, this.currentFocusedIndex, this._checkboxList.length - 1);
        this._checkboxList[this.currentFocusedIndex].focus();
    }
    render() {
        const componentClassNames = this.componentClassNames('cat-c-contextual-menu', {
            'cat-is-active': this.isActive === true,
            'cat-contextual-menu--has-header': this.slots.hasSlotted('header')
        });
        return html `
      <div part="base" class="${componentClassNames}" @keydown=${this.handleKeyDown}>
        <slot part="part-trigger" name="trigger" @click=${this.toggleActive} class="cat-c-contextual-menu__trigger-element"></slot>

        ${this.orientation === 'bottom' && this.isMobile
            ? html ` <cat-drawer
              part="bottom-drawer"
              exportparts=${this.getExportParts('drawer')}
              .orientation=${this.orientation}
              .verticalMaxHeight=${this.verticalMaxHeight}
              ?isActive=${this.isActive || false}
              ?isInline=${this.isInline}
              @bl-drawer-close=${this.close}
            >
              <slot slot="header" part="part-header" name="header"></slot></div>
              <slot
                part="part-default"
                id="list-slot"
                @bl-select=${() => {
                this.toggleActive();
            }}
              ></slot
            ></cat-drawer>`
            : html ` <cat-dropdown-panel
              part="dropdown-panel"
              popover=${ifDefined(this.isPopoverEnabled ? 'manual' : undefined)}
              exportparts=${this.getExportParts('dropdown-panel')}
              placement=${this.position}
              class="cat-c-contextual-menu__dropdown-panel"
              ?hasScroll=${this.hasScroll}
              @bl-select=${() => {
                this.toggleActive();
            }}
            >
              <slot part="part-default" id="list-slot"></slot>
            </cat-dropdown-panel>`}
      </div>
    `;
    }
}
__decorate([
    property({ type: String })
], BlocksContextualMenu.prototype, "position", void 0);
__decorate([
    property({ type: Boolean })
], BlocksContextualMenu.prototype, "hasScroll", void 0);
__decorate([
    property({ type: Boolean })
], BlocksContextualMenu.prototype, "isActive", void 0);
__decorate([
    property({ type: Boolean })
], BlocksContextualMenu.prototype, "isInline", void 0);
__decorate([
    property({ type: String })
], BlocksContextualMenu.prototype, "orientation", void 0);
__decorate([
    property({ type: String })
], BlocksContextualMenu.prototype, "verticalMaxHeight", void 0);
__decorate([
    state()
], BlocksContextualMenu.prototype, "isMobile", void 0);
__decorate([
    state()
], BlocksContextualMenu.prototype, "mobileBreakPoint", void 0);
__decorate([
    query('.cat-c-contextual-menu .cat-c-contextual-menu__dropdown-panel')
], BlocksContextualMenu.prototype, "_catDynamicDropdownPanel", void 0);
if (customElements.get('cat-contextual-menu') === undefined) {
    customElements.define('cat-contextual-menu', BlocksContextualMenu);
}
